/**
 * The sidebar search's pure rules — no React, no network — so the dialog's states are testable on
 * their own (`lib/search-state.test.ts`). `lib/search.ts` is the hook that feeds them.
 */
import type { WebSearchResult } from '../pages/api/search';

/**
 * The shortest query the search sends. One letter matches almost every message, so symphony-bff's
 * hybrid search (a message search, a name search and a parent lookup, back to back) ran into the
 * route's 10s deadline on it — the "Something went wrong" a fast typist met on their first keystroke
 * (2026-10-29). The route applies the same floor, so no caller can send one either.
 */
export const MIN_SEARCH_QUERY_LENGTH = 2;

/** Whether a TRIMMED query is long enough to search. Below it the dialog keeps the recent chats. */
export function isSearchableQuery(trimmed: string): boolean {
  return trimmed.length >= MIN_SEARCH_QUERY_LENGTH;
}

/** A non-2xx answer from `/api/search`, carrying its status so the retry can tell a 5xx apart. */
export class SearchHttpError extends Error {
  constructor(readonly status: number) {
    super(`Search failed (${status})`);
    this.name = 'SearchHttpError';
  }
}

/**
 * One retry, and only for a server-side failure (5xx): a cold symphony-bff search can miss its
 * deadline once and answer the same query in under a second right after. A 4xx is an answer, and an
 * abort is a query the reader has already moved on from.
 */
export function shouldRetrySearch(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && error instanceof SearchHttpError && error.status >= 500;
}

export type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading'; query: string }
  | { kind: 'error'; query: string }
  | { kind: 'ready'; query: string; results: WebSearchResult[] };

export interface SearchStateInput {
  /** What the input holds now, trimmed. */
  trimmed: string;
  /** The debounced query — the one the request below was made for. */
  debounced: string;
  /** Results already cached for `trimmed`, if it was searched before. */
  cached: WebSearchResult[] | undefined;
  /** The request's own answer, for `debounced`. */
  data: WebSearchResult[] | undefined;
  /** The request's error, for `debounced`. */
  error: unknown;
  /** A fetch for `debounced` is in flight (the retry included). */
  fetching: boolean;
}

/**
 * What the dialog shows. A failure is shown only when it belongs to the query in the input, and only
 * once nothing for it is still in flight: an error from a query the reader has typed past never
 * replaces the current results or their loading state.
 */
export function resolveSearchState({ trimmed, debounced, cached, data, error, fetching }: SearchStateInput): SearchState {
  if (!isSearchableQuery(trimmed)) return { kind: 'idle' };
  // A previously-fetched query resolves instantly, skipping the debounce.
  if (cached) return { kind: 'ready', query: trimmed, results: cached };
  // Whatever the request says, it is about another query.
  if (trimmed !== debounced) return { kind: 'loading', query: trimmed };
  if (data) return { kind: 'ready', query: trimmed, results: data };
  if (error && !fetching) return { kind: 'error', query: trimmed };
  return { kind: 'loading', query: trimmed };
}
